import { Injectable, signal } from '@angular/core';
import { DateTime, DurationLike } from 'luxon';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { DeviceDetectorService } from 'ngx-device-detector';
import posthog from 'posthog-js';
import { BehaviorSubject, EMPTY, map, mergeMap, of, take } from 'rxjs';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { environment } from '../../../environments/environment';
import { LambdaService } from '../../shared/core/lambda.service';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { ScreenRecordingService } from '../screen-recording/screen-recording.service';
import { SessionMessage } from '../session-message/session-message';
import { SessionMessageService } from '../session-message/session-message.service';
import { UserClerkService } from '../user/user-clerk.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import {
  CreateSessionRequest,
  EndSessionRequest,
  SessionGate,
  SessionIdRequest,
} from './../../../../../backend/src/core/session/session.gate';
import { Session, SESSION_LENGTH, SessionStatus } from './session';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private _resourceName = ResourceName.session;

  private _changeSubject = new BehaviorSubject<Session | undefined>(undefined);

  tabMedia?: MediaStream;
  micStream?: MediaStream;

  showNotEnoughPointsModal = signal<boolean>(false);

  constructor(
    private deviceService: DeviceDetectorService,
    private notification: NzNotificationService,
    private workspaceHolderService: WorkspaceHolderService,
    private coreItemService: CoreItemService,
    private lambdaService: LambdaService,
    private userClerkService: UserClerkService,
    private sessionMessageService: SessionMessageService,
    private screenRecordingService: ScreenRecordingService
  ) {}

  getRecordingFileUrl(session: Session) {
    if (!session) {
      return '';
    }
    return `${this.workspaceHolderService.currentWorkspace().id}/${session!.parentId}/${session?.id}.webm`;
  }

  stopTabSharing() {
    this.tabMedia?.getTracks().forEach((track) => track.stop());
    this.tabMedia = undefined;
  }

  stopMicSharing() {
    this.micStream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.micStream = undefined;
  }

  isCreditsEnough(session: Session) {
    const type = session.type;
    const credits = this.userClerkService.credits();
    return true; // isCreditsEnoughForSessionType(type, credits);
  }

  pingSession$(session: Session) {
    if (session.isPaid) {
      return of({
        isSuccess: true,
      });
    }

    const request: SessionIdRequest = { id: session.id };
    return this.lambdaService
      .run$(this._resourceName, SessionGate.actions.pingSession, request, { notChangeLoadingStatus: true })
      .pipe(
        map((data) => {
          console.log('pingSession$:', data);

          if (data.id && data.sk) {
            const frontend = this.coreItemService.updateCoreItemInCache(data) as Session;
            this._changeSubject.next(frontend);
            return {
              isSuccess: true,
            };
          } else {
            return data;
          }
        })
      );
  }

  getSessionList$(startTime: DateTime, endTime: DateTime) {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService
          .getCoreItemList$({
            coreTypeList: [CoreItemType.session],
            dateRange: { startTime, endTime },
          })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as Session[];
            })
          );
      })
    );
  }

  getRecentSessionList$(duration: DurationLike) {
    const startTime = DateTime.local().minus(duration);
    const endTime = DateTime.local();

    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.getSessionList$(startTime, endTime);
      })
    );
  }

  getSessionByCreatedById$() {
    // TODO:
  }

  getSessionByTemplateId$(templateIdList: string[]) {
    return this.coreItemService
      .getCoreItemList$({ coreTypeList: [CoreItemType.session], parentIdList: templateIdList })
      .pipe(
        map((list) => {
          list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
          return list as Session[];
        })
      );
  }

  getSessionById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Session));
  }

  isOngoing(session?: Session) {
    if (!session) {
      return false;
    }
    if (session.status && session.statusTime) {
      if (session.status === SessionStatus.done) {
        return false;
      }

      const diff = -session.statusTime.diffNow('minutes').minutes;

      if (session.status === SessionStatus.ongoing && diff <= SESSION_LENGTH) {
        return true;
      } else if (session.status === SessionStatus.ongoing && diff > SESSION_LENGTH) {
        session.status = SessionStatus.done;

        const fileUrl = this.getRecordingFileUrl(session);
        this.screenRecordingService.stopRecordingAndSaveToWebm(fileUrl);

        this.endSession$(session, `${environment.filesDomain}/${fileUrl}`).pipe(take(1)).subscribe();
      }
    }
    return false;
  }

  launchSession$(session: Session) {
    if (this.deviceService.browser !== 'Chrome' && this.deviceService.browser !== 'MS-Edge-Chromium') {
      this.notification.error('Not Support', 'Please use Chrome browser to use this feature.', { nzDuration: 10000 });
      return EMPTY;
    }

    if (!this.deviceService.isDesktop()) {
      this.notification.error('Not Support', 'Please use a computer to use this feature.', { nzDuration: 10000 });
      return EMPTY;
    }

    const request: SessionIdRequest = {
      id: session.id,
    };

    return this.lambdaService.run$(this._resourceName, SessionGate.actions.launchSession, request).pipe(
      map((response) => {
        const front = this.coreItemService.updateCoreItemInCache(response) as Session;
        this._changeSubject.next(front);
        posthog.capture(PostHogEvent.sessionLaunch);
        return front;
      })
    );
  }

  launchFreeTrialSession$(session: Session) {
    if (this.deviceService.browser !== 'Chrome' && this.deviceService.browser !== 'MS-Edge-Chromium') {
      this.notification.error('Not Support', 'Please use Chrome browser to use this feature.', { nzDuration: 10000 });
      return of(null);
    }

    if (!this.deviceService.isDesktop()) {
      this.notification.error('Not Support', 'Please use a computer to use this feature.', { nzDuration: 10000 });
      return of(null);
    }

    const request: SessionIdRequest = {
      id: session.id,
    };

    return this.lambdaService.run$(this._resourceName, SessionGate.actions.launchFreeTrialSession, request).pipe(
      map((response) => {
        if (response) {
          if (response.id) {
            const front = this.coreItemService.updateCoreItemInCache(response) as Session;
            this._changeSubject.next(front);

            posthog.capture(PostHogEvent.sessionLaunch);

            this.userClerkService.reloadUser$().pipe(take(1)).subscribe();
          }
        }
        return response;
      })
    );
  }

  endSession$(session: Session, recordingUrl: string) {
    const request: EndSessionRequest = {
      sessionId: session.id,
      recordingUrl: recordingUrl,
    };

    posthog.capture(PostHogEvent.sessionLaunch);

    return this.lambdaService.run$(this._resourceName, SessionGate.actions.endSession, request).pipe(
      map((response) => {
        const front = this.coreItemService.updateCoreItemInCache(response) as Session;
        this._changeSubject.next(front);
        return front;
      })
    );
  }

  update$(item: Session) {
    posthog.capture(PostHogEvent.sessionUpdate);
    return this.coreItemService.updateCoreItem$(item);
  }

  addSession$(templateId: string) {
    if (this.deviceService.browser !== 'Chrome' && this.deviceService.browser !== 'MS-Edge-Chromium') {
      this.notification.error('Not Support', 'Please use Chrome browser to use this feature.', { nzDuration: 10000 });
      return EMPTY;
    }

    if (!this.deviceService.isDesktop()) {
      this.notification.error('Not Support', 'Please use a computer to use this feature.', { nzDuration: 10000 });
      return EMPTY;
    }

    posthog.capture(PostHogEvent.sessionAdd);

    const request: CreateSessionRequest = {
      templateId,
    };

    return this.lambdaService.run$(this._resourceName, SessionGate.actions.createSession, request).pipe(
      map((data) => {
        // this.coreItemService.addItemInFrontend(this.coreItemService.convertFromBackendToFrontend(data));
        this._changeSubject.next(data as Session);
        return data as Session;
      })
    );
  }

  deleteSession$(session: Session) {
    const deleteMetadataList = [session].map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        mergeMap((_) => {
          posthog.capture(PostHogEvent.sessionDelete);
          this._changeSubject.next(undefined);
          return this.sessionMessageService
            .getSessionMessageListById$(session.id)
            .pipe(
              mergeMap((dataList) => this.sessionMessageService.deleteSessionMessageList$(dataList as SessionMessage[]))
            );
        })
      );
  }
}
